// The studio session: one immutable draft in undo/redo history, everything else
// derived. Shared by the full-page studio and the planogram side-panel overlay.
import { useMemo, useState } from "react";
import { Pog } from "@CommonTypes/merchflow/pog/pog";
import {
  POG_PREFERRED_ORDER,
  type PogAdapterOptions,
} from "@CommonUtils/cdtStudio/adapters/pogToStudioItems";
import { studioItemsOf } from "../adapters/studioItemsOf";
import { buildAsBuiltLayout, buildLayout } from "../domain/buildLayout";
import { auditContiguity } from "../domain/contiguityAudit";
import {
  draftFromInference,
  inferSpine,
  withPlanFragmentJoin,
  type AttributeCandidate,
  type InferenceOptions,
} from "../domain/spineInference";
import {
  canRedo,
  canUndo,
  clearRedo,
  createStudioHistory,
  recordSnapshot,
  redo,
  undo,
  type StudioHistory,
} from "../domain/studioHistory";
import { getStudioHistory, setStudioHistory, studioHistoryKey } from "./studioHistoryStore";
import { isFlagSettled, keptAnswersOf, withKeptAnswers } from "../domain/studioTransactions";
import { getKeptAnswers, keepAnswers } from "./studioQuestionAnswers";
import {
  Flag,
  FlagSeverity,
  PATH_SEPARATOR,
  StudioDraft,
  type StudioItem,
  type StudioRunGeometry,
  type WallBlockingKind,
  effectiveAttrs,
} from "../domain/studioTypes";
import { chooseWallBlocking } from "../domain/horizontalBlocking";
import { detectExceptions } from "../resolver/detectExceptions";
import { projectTree } from "../projections/treeProjection";

export const blockingFlagsOnly = (flags: readonly Flag[]): Flag[] =>
  flags.filter((flag) => flag.severity === "blocking");

export interface StudioSessionOptions {
  adapterOptions?: PogAdapterOptions;
  inferenceOptions?: InferenceOptions;
  /** @deprecated Prefer inferenceOptions.preferredOrder. */
  preferredOrder?: readonly string[];
  /** Base pog id, so history survives unmount. Omitted off-route: no persistence. */
  basePogId?: number | null;
  /** Stable attributes that define provenance blocks, major to minor. */
  blockIdentityAttributes?: readonly string[];
  /** Attribute whose values use quantity-aware resolver ordering. */
  quantityAttribute?: string;
  /** Attribute treated as the terminal variant by exception taxonomy. */
  terminalVariantAttribute?: string;
  /** The brand role: a brand the base stands in stretches a sub-brand explains is offered as two. */
  brandAttribute?: string;
  /** Split-off offers the merchant answered No to: raised as declined, explaining nothing. */
  declinedSplitOffIds?: ReadonlySet<string>;
  /** Names levels the configuration declares but the load does not carry. Never an
   *  inference input. */
  declaredValues?: Readonly<Record<string, Readonly<Record<string, string | null>>>>;
  declaredLabels?: Readonly<Record<string, string>>;
  /** The plan the studio opened on, when the session works on another (a plan laid from the
   *  tree). The brand split-off and size-tag questions are decisions about this plan, so
   *  `asBuiltFlags` are detected on it. Omitted, or the same plans: `asBuiltFlags` is `flags`. */
  asBuiltPogs?: readonly Pog[];
}

/** Does this draft still describe the attribute table the data now offers? */
export const draftSpeaksAttributeTable = (
  draft: Pick<StudioDraft, "spine">,
  candidates: readonly AttributeCandidate[],
): boolean => {
  const live = new Set(candidates.map((candidate) => candidate.attr));
  return draft.spine.every((level) => live.has(level.attr));
};

export const useStudioSession = (pogs: readonly Pog[], options: StudioSessionOptions = {}) => {
  const {
    adapterOptions,
    inferenceOptions,
    preferredOrder,
    blockIdentityAttributes,
    quantityAttribute,
    terminalVariantAttribute,
    brandAttribute,
    declinedSplitOffIds,
    declaredValues,
    declaredLabels,
    basePogId,
    asBuiltPogs,
  } = options;
  const historyKey = studioHistoryKey(basePogId);

  const { items, runs, candidates } = useMemo(
    () => studioItemsOf(pogs, adapterOptions),
    [pogs, adapterOptions],
  );

  /** Inference's draft for this load, optionally with the wall's reading forced (a flip on an
   *  untouched draft). Cached, so the same reading always returns the same draft. The merchant's
   *  kept answers (a size layout, a size-tag Yes: `keepAnswers`) go back on it, so a reload or a
   *  re-inference asks nothing already answered. */
  const inferredDraftFor = useMemo(() => {
    const infer = (wallBlocking?: WallBlockingKind) =>
      draftFromInference(
        inferSpine(items, candidates, runs, {
          // A saved legacy join is read against the plan the session opened.
          ...withPlanFragmentJoin(inferenceOptions ?? {}, pogs[0]?.planogram.bays ?? []),
          preferredOrder: preferredOrder ?? inferenceOptions?.preferredOrder ?? POG_PREFERRED_ORDER,
          // Raw size sequences; it must not take a blocking rung ahead of a level like sub-brand.
          quantityAttribute: inferenceOptions?.quantityAttribute ?? quantityAttribute,
          ...(wallBlocking ? { wallBlocking } : {}),
        }),
      );
    const asRead = infer();
    const flipped = new Map<WallBlockingKind, StudioDraft>();
    const kept = (draft: StudioDraft) => withKeptAnswers(draft, getKeptAnswers(historyKey));
    return (wallBlocking?: WallBlockingKind): StudioDraft => {
      if (!wallBlocking || !asRead.wallBlocking || asRead.wallBlocking.read === wallBlocking) {
        return kept(asRead);
      }
      const draft = flipped.get(wallBlocking) ?? infer(wallBlocking);
      flipped.set(wallBlocking, draft);
      return kept(draft);
    };
  }, [
    items,
    candidates,
    runs,
    pogs,
    preferredOrder,
    inferenceOptions,
    quantityAttribute,
    historyKey,
  ]);
  const initialDraft = useMemo(() => inferredDraftFor(), [inferredDraftFor]);
  // Seeded from the module-level store and written through to it, so the session
  // survives unmount — but only a draft that still speaks this attribute table;
  // a stale-vocabulary draft is discarded for a fresh inference.
  const [history, setHistoryState] = useState(() => {
    const stored = getStudioHistory(historyKey);
    return stored && draftSpeaksAttributeTable(stored.present, candidates)
      ? stored
      : createStudioHistory(initialDraft);
  });
  const setHistory = (update: (current: StudioHistory) => StudioHistory) => {
    setHistoryState((current) => {
      const next = update(current);
      setStudioHistory(historyKey, next);
      // The answers on the present draft outlive the page (undo and redo included).
      keepAnswers(historyKey, keptAnswersOf(next.present));
      return next;
    });
  };
  const draft = history.present;

  const baseLayout = useMemo(() => buildAsBuiltLayout(items), [items]);
  const layout = useMemo(() => buildLayout(items, draft, runs), [items, draft, runs]);

  // Detected on the as-built plan with the resolver's own audit measures, so an
  // exception is raised exactly when studio.check() would call it unresolved.
  const detectOn = useMemo(
    () => (onItems: readonly StudioItem[], onRuns: readonly StudioRunGeometry[]) =>
      detectExceptions(onItems, onRuns, draft, {
        quantityAttribute,
        crossLevelNameFlags: false,
        blockIdentityAttributes,
        verticalBlockShare: inferenceOptions?.verticalBlockShare,
        terminalVariantAttribute,
        brandAttribute,
        declinedSplitOffIds,
        declaredValues,
        declaredLabels,
        rowReplication: true,
      }),
    [
      draft.spine,
      draft.valueOrder,
      draft.attrOverrides,
      /* A branch-local level move touches only these; omitting them staled the census. */
      draft.scopedLevelOrder,
      draft.scopedValueOrder,
      draft.levelBands,
      quantityAttribute,
      blockIdentityAttributes,
      inferenceOptions?.verticalBlockShare,
      terminalVariantAttribute,
      brandAttribute,
      declinedSplitOffIds,
      declaredValues,
      declaredLabels,
    ],
  );
  const detected = useMemo(() => detectOn(items, runs), [detectOn, items, runs]);
  // The studio shows blocking findings only; "to check" and "to review" rows are advisory
  // wording about the tree and were read as violations. They stay in the detector for tests.
  const flags = blockingFlagsOnly(detected.flags);

  // Once the session works on a plan laid from the tree, that plan no longer raises some questions
  // (it already stands all Baby with all), so they are detected on the plan the studio opened on.
  const asBuiltItems = useMemo(
    () =>
      asBuiltPogs &&
      (asBuiltPogs.length !== pogs.length || asBuiltPogs.some((pog, at) => pog !== pogs[at]))
        ? studioItemsOf(asBuiltPogs, adapterOptions)
        : null,
    [asBuiltPogs, pogs, adapterOptions],
  );
  const asBuiltDetected = useMemo(
    () => (asBuiltItems ? detectOn(asBuiltItems.items, asBuiltItems.runs) : null),
    [detectOn, asBuiltItems],
  );
  const asBuiltFlags = asBuiltDetected ? blockingFlagsOnly(asBuiltDetected.flags) : flags;

  const blockOf = useMemo(() => {
    const attrs =
      blockIdentityAttributes?.filter((attr) => draft.spine.some((level) => level.attr === attr)) ??
      draft.spine.slice(0, 2).map((level) => level.attr);
    return (item: (typeof items)[number]) => {
      const values = effectiveAttrs(item, draft);
      return attrs.map((attr) => values[attr] ?? "").join(PATH_SEPARATOR);
    };
  }, [draft, blockIdentityAttributes]);

  const provenance = useMemo(
    () => auditContiguity(baseLayout, layout, items, blockOf, runs),
    [baseLayout, layout, items, blockOf, runs],
  );

  const tree = useMemo(() => projectTree(items, draft, flags), [items, draft, flags]);

  const settledFlagIds = useMemo(
    () => new Set(flags.filter((flag) => isFlagSettled(draft, flag.id)).map((flag) => flag.id)),
    [flags, draft],
  );
  const openFlags = useMemo(
    () => flags.filter((flag) => !settledFlagIds.has(flag.id)),
    [flags, settledFlagIds],
  );
  const severityCounts = useMemo(() => {
    const counts: Record<FlagSeverity, number> = { blocking: 0, toCheck: 0, toReview: 0 };
    for (const flag of openFlags) counts[flag.severity] += 1;
    return counts;
  }, [openFlags]);

  const commit = (next: StudioDraft) => {
    if (next !== draft) setHistory((current) => recordSnapshot(current, next));
  };

  /** The merchant flips the wall's reading: one undoable step. An untouched draft re-infers
   *  with the new reading; an edited one keeps its edits (`chooseWallBlocking`). */
  const chooseWallReading = (blocking: WallBlockingKind) =>
    commit(chooseWallBlocking(history, blocking, (kind) => inferredDraftFor(kind)));

  /** SKUs under a tree node (scope path key), for highlighting. */
  const skuIdsUnderNode = (nodeId: string): string[] => {
    const values = nodeId.split(PATH_SEPARATOR);
    return items
      .filter((item) => {
        const attrs = effectiveAttrs(item, draft);
        let valueIndex = 0;
        for (const level of draft.spine) {
          if (valueIndex >= values.length) break;
          if ((attrs[level.attr] ?? "") === values[valueIndex]) valueIndex += 1;
        }
        return valueIndex >= values.length;
      })
      .map((item) => item.skuId);
  };

  return {
    items,
    runs,
    candidates,
    draft,
    commit,
    chooseWallReading,
    undo: () => setHistory((current) => undo(current)),
    redo: () => setHistory((current) => redo(current)),
    /** Voids what Redo holds, as a commit does: for an action taken outside the draft. */
    clearRedo: () => setHistory((current) => clearRedo(current)),
    canUndo: canUndo(history),
    canRedo: canRedo(history),
    /** The draft Undo would bring back, if any. */
    undoDraft: history.past[history.past.length - 1] as StudioDraft | undefined,
    /** The draft Redo would bring back, if any. */
    redoDraft: history.future[0] as StudioDraft | undefined,
    /** Committed edits between here and the untouched plan. At 1 the next undo lands
     *  on the base, and the caller must restore the AS-BUILT view, not rebuild it. */
    undoDepth: history.past.length,
    baseLayout,
    layout,
    flags,
    /** The questions' detection: on the plan the studio opened on (`asBuiltPogs`). */
    asBuiltFlags,
    resolverInputs: detected.inputs,
    openFlags,
    settledFlagIds,
    severityCounts,
    provenance,
    tree,
    skuIdsUnderNode,
  };
};

export type StudioSession = ReturnType<typeof useStudioSession>;

/** All flags (settled included) sorted blocking-first for list views. */
export const sortFlagsBySeverity = (flags: readonly Flag[]): Flag[] => {
  const rank: Record<FlagSeverity, number> = { blocking: 0, toCheck: 1, toReview: 2 };
  return [...flags].sort((a, b) => rank[a.severity] - rank[b.severity]);
};
